import LoadingCircle from "reports/components/simple/LoadingCircle";
import { PnlBreakdown } from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { getMetricUnit, SummaryMetricInput } from "reports/utils/metricUtils";
import {
  CHART_HEIGHT,
  pnlBreakdownToMetricChartData,
  RECHARTS_FULL_WIDTH,
} from "../BespokeReports/bespokeReportUtils";
import CardWrapper from "../CardWrapperStyles";
import { TopBarLabel } from "../RechartHelpers/RechartBarLabels";
import RechartLegend from "../RechartHelpers/RechartLegend";
import GenericBiAxialComposedChart from "./GenericBiAxialComposedChart";
type ScaleType = "number" | "percentage";

export const MetricTrendChartStyles = styled.div`
  display: flex;
  justify-content: center;

  .chart-container {
    overflow: auto;
    width: 100%;
    height: calc(100% + 30px);
  }

  .chart-wrapper {
    display: flex;
    justify-content: center;
    min-width: 800px;
    width: fit-content;
    height: 504px;
  }

  .recharts-cartesian-axis-tick-value {
    z-index: 1000;
    position: relative;
  }

  .recharts-layer.recharts-cartesian-axis {
    z-index: 1000;
  }
`;

export function MetricsTrendChart({
  summaryMetricInputList,
  pnlBreakdowns,
  numWeeks = 10,
  width = RECHARTS_FULL_WIDTH,
  height = CHART_HEIGHT,
  descendingOrder = true,
}: {
  summaryMetricInputList: SummaryMetricInput[];
  pnlBreakdowns: PnlBreakdown[];
  numWeeks?: number;
  width?: number;
  height?: number;
  descendingOrder?: boolean;
}) {
  const { t } = useTranslation("EOSReport");
  const { chartData } = pnlBreakdownToMetricChartData({
    pnlBreakdowns,
    summaryMetricInputList,
    t,
    descendingOrder,
  });

  const METRIC_COLORS = [
    "#4e79a7",
    "#e15759",
    "#59a14f",
    "#9c755f",
    "#76b7b2",
    "#af7aa1",
    "#ff9da7",
    "#edc949",
    "#bab0ab",
    "#f28e2c",
  ];

  const bars = summaryMetricInputList
    .filter(metric => metric.dataDumpKey)
    .map((metric, index) => {
      const metricName = metric.title ?? metric.name ?? "";
      const metricUnit = metric.unit ?? getMetricUnit(metricName);
      const isHours = metricName.toLowerCase().includes("hours");
      const isFeet = metricName.toLowerCase().includes("feet");
      const isPercentage = metricUnit === "%";
      console.log("Metric config:", {
        name: metric.name,
        unit: metricUnit,
        color: METRIC_COLORS[index],
        index,
      });
      const color = metric.color || METRIC_COLORS[index % METRIC_COLORS.length];
      const formatValue = (value: number, unit?: string) => {
        if (unit === "feet") {
          return `${Math.round(value / 1000)}k`;
        }
        return value;
      };

      return {
        key: metric.name,
        dataKey: metric.name,
        name: `${metricName} ${metricUnit ? `(${metricUnit})` : ""}`,
        fill: color,
        label: {
          content: TopBarLabel({
            suffix: metricUnit,
            textColor: color,
            formatter: isFeet
              ? (value: number) => formatValue(value, "feet")
              : undefined,
          }),
        },
        yAxisId: isHours ? "hours" : isFeet ? "feet" : "percentage",
        scaleType: (isPercentage ? "percentage" : "number") as ScaleType,
        domain: isPercentage ? [0, 100] : ("auto" as const),
      };
    });

  return (
    <CardWrapper>
      <div className="header">Weekly Trend</div>
      {pnlBreakdowns.length === numWeeks ? (
        <MetricTrendChartStyles>
          <div className="chart-container">
            <div className="chart-wrapper">
              <GenericBiAxialComposedChart
                data={chartData}
                {...{ bars, width: width + 20, height }}
                xLabel={"Weeks"}
                axes={[
                  {
                    id: "percentage",
                    label: "Metrics %",
                    orientation: "left",
                    scaleType: "percentage" as ScaleType,
                    domain: [0, 100],
                  },
                  {
                    id: "hours",
                    label: "Hours",
                    orientation: "right",
                    scaleType: "number" as ScaleType,
                    domain: "auto" as const,
                  },
                  {
                    id: "feet",
                    label: "Feet",
                    orientation: "right",
                    scaleType: "number" as ScaleType,
                    domain: "auto" as const,
                  },
                ]}
                legend={RechartLegend({ width })}
              />
            </div>
          </div>
        </MetricTrendChartStyles>
      ) : (
        <LoadingCircle />
      )}
    </CardWrapper>
  );
}
